import { useState } from 'react';

import {
  Button,
  Center,
  Flex,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import {
  ActionIconButton,
  AddCircleOutline,
  CompanyIdentityProvider,
  DeleteOutline,
  EditOutline,
  IdentityProviderType,
  useToast,
} from '@aslanpay/portal-common-lib';
import { format, parseISO } from 'date-fns';

import TextWithClipboard from 'app/components/UI/TextWithClipboard';
import useDeleteIdentityProvider from 'app/hooks/useDeleteIdentityProvider';
import useFetchIdentityProviders from 'app/hooks/useFetchIdentityProviders';

import { IdentityProviderModal } from '../IdentityProviderModal';

import { IdentityProvidersSectionProps } from './IdentityProvidersSection.types';

const PROVIDER_TYPE_LABELS: Record<IdentityProviderType, string> = {
  ENTRA_OIDC: 'Microsoft Entra (OIDC)',
};

// Client-facing documentation to hand to the company so they can register the Entra app on their end.
const ENTRA_SETUP_GUIDE_URL =
  'https://www.notion.so/aslan-benefit/SSO-Setup-in-Microsoft-Entra-30c621920ecf8096a802f411de87ace1';

function IdentityProvidersSection({ companyId = '' }: IdentityProvidersSectionProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingProvider, setEditingProvider] = useState<CompanyIdentityProvider | undefined>(
    undefined
  );
  const toast = useToast();

  const deleteMutation = useDeleteIdentityProvider();
  const { data: identityProviders, isLoading } = useFetchIdentityProviders(companyId);

  const hasProviders = !!identityProviders && identityProviders.length > 0;

  const handleAdd = () => {
    setEditingProvider(undefined);
    onOpen();
  };

  const handleEdit = (provider: CompanyIdentityProvider) => {
    setEditingProvider(provider);
    onOpen();
  };

  const handleDelete = async (id: string) => {
    try {
      if (!companyId) return;
      await deleteMutation.mutateAsync({ companyId, id });
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Identity provider deleted',
        description: 'The SSO identity provider has been successfully deleted.',
      });
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the identity provider could not be deleted. Please try again.',
      });
    }
  };

  return (
    <Flex flexDir="column" gap="6px">
      <Text as="h6" textStyle="h6SemiBold">
        Single sign-on (SSO)
      </Text>
      <Text textStyle="extraSmallRegular" color="brand.darkGray">
        Let employees sign in with their organisation&apos;s Microsoft Entra account instead of a
        password. Add the company&apos;s Entra identity provider here using the Directory (tenant)
        ID and Application (client) ID from their app registration. Only one provider can be
        configured per company. Share the{' '}
        <Link
          href={ENTRA_SETUP_GUIDE_URL}
          isExternal
          color="brand.purple"
          textDecoration="underline"
          data-testid="entra-setup-guide-link"
        >
          client setup guide
        </Link>{' '}
        with the company so they can register the app and send you their tenant and client IDs.
      </Text>

      {isLoading && (
        <Center py="40px">
          <Spinner color="brand.purple" size="md" />
        </Center>
      )}

      {!isLoading && hasProviders && (
        <Flex
          mt="14px"
          overflowX="auto"
          border="1px solid"
          borderColor="brand.lightGray1"
          borderRadius="8px"
        >
          <Table variant="simple" size="sm">
            <Thead bgColor="brand.lightGray2">
              <Tr>
                <Th>Provider</Th>
                <Th>Tenant ID</Th>
                <Th>Client ID</Th>
                <Th>Created At</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {identityProviders?.map((provider: CompanyIdentityProvider) => (
                <Tr key={provider.id}>
                  <Td fontSize="sm">{PROVIDER_TYPE_LABELS[provider.type] ?? provider.type}</Td>
                  <Td fontSize="sm" fontFamily="Inconsolata, monospace">
                    <TextWithClipboard value={provider.tenantId} />
                  </Td>
                  <Td fontSize="sm" fontFamily="Inconsolata, monospace">
                    <TextWithClipboard value={provider.clientId} />
                  </Td>
                  <Td fontSize="sm">
                    {provider.createdAt
                      ? format(parseISO(provider.createdAt), 'dd/MM/yyyy HH:mm')
                      : '-'}
                  </Td>
                  <Td>
                    <Flex justify="center" gap="8px">
                      <ActionIconButton
                        icon={<EditOutline boxSize="16px" />}
                        aria-label="Edit identity provider"
                        onClick={() => handleEdit(provider)}
                      />
                      <ActionIconButton
                        icon={<DeleteOutline boxSize="16px" />}
                        aria-label="Delete identity provider"
                        colorScheme="red"
                        isLoading={deleteMutation.isPending}
                        onClick={() => handleDelete(provider.id)}
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Flex>
      )}

      {!isLoading && !hasProviders && (
        <Flex
          mt="14px"
          w="100%"
          py="40px"
          px="20px"
          bgColor="brand.lightGray2"
          borderRadius="8px"
          justify="center"
          align="center"
          flexDir="column"
          gap="16px"
        >
          <Text textStyle="body" color="brand.darkGray">
            No SSO identity provider configured yet
          </Text>
          <Button leftIcon={<AddCircleOutline boxSize="20px" />} size="sm" onClick={handleAdd}>
            Add identity provider
          </Button>
        </Flex>
      )}

      <IdentityProviderModal
        companyId={companyId}
        isOpen={isOpen}
        onClose={onClose}
        identityProvider={editingProvider}
      />
    </Flex>
  );
}

export { IdentityProvidersSection };

export default IdentityProvidersSection;
